import { ApproverSlot } from '@/types/mold';
import { CheckCircle, Clock, Shield, ThumbsDown, ThumbsUp, UserIcon, XCircle } from 'lucide-react';
import ApprovalSlotComments from './ApprovalSlotComments';
import { formatDateTimeFromString } from '@/utils/dateUtils';

interface ApprovalSlotProps {
    slot: ApproverSlot;
    index: number;
    isCurrentSlot: boolean;
    isCurrentUserSlot: boolean;
    onApprovalAction?: (action: 'accept' | 'reject' | 'rework') => void;
    displayMode?: 'default' | 'readonly';
}

// Status configuration constants
const STATUS_CONFIG = {
    approved: {
        container: 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-300 shadow-sm',
        indicator: 'bg-gradient-to-br from-green-500 to-green-600 text-white shadow-green-200',
        badge: { bg: 'bg-green-100 text-green-700', text: 'อนุมัติแล้ว', icon: CheckCircle },
        stepIcon: CheckCircle
    },
    rejected: {
        container: 'bg-gradient-to-r from-red-50 to-rose-50 border-red-300 shadow-sm',
        indicator: 'bg-gradient-to-br from-red-500 to-red-600 text-white shadow-red-200',
        badge: { bg: 'bg-red-100 text-red-700', text: 'ปฏิเสธ', icon: XCircle },
        stepIcon: XCircle
    },
    pending: {
        container: 'bg-gradient-to-r from-white to-gray-50 border-gray-200 hover:border-gray-300 hover:shadow-sm',
        indicator: 'bg-gradient-to-br from-gray-300 to-gray-400 text-gray-600 shadow-gray-200',
        badge: { bg: 'bg-blue-100 text-blue-700', text: 'รออนุมัติ', icon: Clock },
        stepIcon: null
    }
} as const;

const CURRENT_SLOT_STYLES = {
    container: 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-300 shadow-md ring-2 ring-blue-100',
    indicator: 'bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-blue-200 ring-2 ring-blue-200'
} as const;

const ACTION_BUTTON_STYLES = {
    base: 'group flex items-center justify-center gap-1 sm:gap-2 px-4 py-2.5 sm:px-6 sm:py-3 text-white text-xs sm:text-sm font-semibold rounded-lg sm:rounded-xl transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 active:scale-95 flex-1',
    approve: 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700',
    reject: 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700'
} as const;

const getSlotStyles = (slot: ApproverSlot, isCurrentSlot: boolean) => {
    const config = STATUS_CONFIG[slot.status];
    return {
        container: isCurrentSlot ? CURRENT_SLOT_STYLES.container : config.container,
        indicator: isCurrentSlot ? CURRENT_SLOT_STYLES.indicator : config.indicator
    };
};

const renderStatusBadge = (slot: ApproverSlot, isCurrentSlot: boolean) => {
    // Only show badge for non-pending status, or pending status that is current slot
    const shouldShowBadge = slot.status !== 'pending' || (slot.status === 'pending' && isCurrentSlot);

    if (!shouldShowBadge) {
        return null;
    }

    const config = STATUS_CONFIG[slot.status];
    const IconComponent = config.badge.icon;

    return (
        <div className={`flex items-center gap-1 px-2 py-1 rounded-full ${config.badge.bg}`}>
            <IconComponent className="h-3 w-3" />
            <span className="text-xs font-medium hidden sm:inline">{config.badge.text}</span>
        </div>
    );
};

interface ActionButtonProps {
    action: 'accept' | 'reject';
    onApprovalAction?: (action: 'accept' | 'reject' | 'rework') => void;
}

const ActionButton: React.FC<ActionButtonProps> = ({ action, onApprovalAction }) => {
    const isAccept = action === 'accept';
    const IconComponent = isAccept ? ThumbsUp : ThumbsDown;
    const buttonStyle = isAccept ? ACTION_BUTTON_STYLES.approve : ACTION_BUTTON_STYLES.reject;
    const text = isAccept ? 'อนุมัติ' : 'ปฏิเสธ';

    return (
        <button
            onClick={() => onApprovalAction ? onApprovalAction(action) : null}
            className={`${ACTION_BUTTON_STYLES.base} ${buttonStyle}`}
        >
            <IconComponent className="h-3 w-3 sm:h-4 sm:w-4 transition-transform group-hover:scale-110" />
            <span>{text}</span>
        </button>
    );
}

const ApprovalSlot: React.FC<ApprovalSlotProps> = ({
    slot,
    index,
    isCurrentSlot,
    isCurrentUserSlot,
    onApprovalAction,
    displayMode = 'default',
}) => {
    const styles = getSlotStyles(slot, isCurrentSlot);
    const statusConfig = STATUS_CONFIG[slot.status];
    const StepIconComponent = statusConfig.stepIcon;

    return (
        <div className={`p-3 sm:p-4 rounded-lg sm:rounded-xl border-2 transition-all duration-300 ${styles.container}`}>
            <div className="space-y-3">
                {/* Top row - Step indicator and approver info */}
                <div className="flex flex-row items-center gap-2 sm:gap-3">
                    <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center text-xs sm:text-sm font-bold shadow-md transition-all duration-200 flex-shrink-0 ${styles.indicator}`}>
                        {StepIconComponent ? (
                            <StepIconComponent className="h-3 w-3 sm:h-4 sm:w-4" />
                        ) : (
                            index + 1
                        )}
                    </div>

                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1 sm:gap-2 mb-1">
                            {slot.approverType === 'role' ? (
                                <>
                                    <Shield className="h-3 w-3 sm:h-4 sm:w-4 text-purple-600 flex-shrink-0" />
                                    <span className="text-xs text-purple-600 font-medium">แผนก</span>
                                </>
                            ) : (
                                <>
                                    <UserIcon className="h-3 w-3 sm:h-4 sm:w-4 text-blue-600 flex-shrink-0" />
                                    <span className="text-xs text-blue-600 font-medium">ผู้อนุมัติ</span>
                                </>
                            )}
                        </div>
                        <div className="text-sm sm:text-base font-semibold text-slate-900 leading-tight mb-1">
                            {slot.approverType === 'role' ? (
                                <span title={`แผนก ${slot.assignedRole?.role}`}>
                                    {slot.assignedRole?.role}
                                </span>
                            ) : (
                                <span title={slot.assignedUser?.fullname || slot.assignedUser?.username}>
                                    {slot.assignedUser?.fullname || slot.assignedUser?.username}
                                </span>
                            )}
                        </div>
                        {/* Actual approver info - integrated in the main approver section */}
                        {slot.actualApproverUser && (
                            <>
                                <div className="text-xs text-gray-600 leading-relaxed">
                                    <span className="text-gray-500">ผู้อนุมัติ: </span>
                                    <span className="font-medium text-gray-700">
                                        {slot.actualApproverUser.fullname || slot.actualApproverUser.username}
                                    </span>
                                </div>
                                {slot.approvedAt && <div className="text-xs text-gray-600 leading-relaxed">
                                    <span className="text-gray-500">เมื่อ: </span>
                                    <span className="font-medium text-gray-700">
                                        {formatDateTimeFromString(slot.approvedAt)}
                                    </span>
                                </div>}
                            </>
                        )}
                    </div>

                    {/* Status indicator - only for non-interactive slots */}
                    {!isCurrentUserSlot && (
                        <div className="flex-shrink-0">
                            {renderStatusBadge(slot, isCurrentSlot)}
                        </div>
                    )}
                </div>

                {/* Comments section */}
                {slot.comments && (
                    <ApprovalSlotComments
                        comments={slot.comments}
                        status={slot.status}
                    />
                )}

                {/* Action buttons for current user */}
                {isCurrentUserSlot && displayMode === 'default' && (
                    <div className="pt-2 border-t border-gray-200">
                        <div className="flex gap-2 sm:gap-3">
                            <ActionButton action="accept" onApprovalAction={onApprovalAction} />
                            <ActionButton action="reject" onApprovalAction={onApprovalAction} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ApprovalSlot;
